import { create } from 'zustand'

import { env } from '@/config/env'
import { setUnauthorizedHandler } from '@/lib/api/auth-token'
import { msalInstance } from '@/lib/auth/msal'

import type { PortalRole, User } from '../types'

/** Stand-in identity used while `VITE_REQUIRE_AUTH` is off, so the shell has a user to show. */
const LOCAL_USER: User = {
  id: 'local',
  name: 'Local User',
  email: 'local@techquarter.io',
  roles: ['Administrator'],
  status: 'Active',
  personId: null,
  personName: null,
  lastSignedInAt: null,
}

type AuthState = {
  /** The portal profile — roles and all — fetched from the backend after sign-in. */
  user: User | null
  /**
   * Mirrors "does MSAL have a signed-in account". Route guards read this
   * instead of calling `useMsal()` directly, so they work the same whether or
   * not an `MsalProvider` is even mounted (it isn't, when auth is off).
   */
  hasEntraAccount: boolean
  /**
   * True when the directory let them in but the portal did not: signed in
   * against the tenant, never invited. Distinct from being signed out, because
   * sending them back to the sign-in page would only loop.
   */
  isAccessDenied: boolean
  /** False until startup work (MSAL, then the backend profile fetch) is done. */
  isHydrated: boolean
  setAccount: (hasAccount: boolean) => void
  setUser: (user: User) => void
  setAccessDenied: () => void
  setHydrated: () => void
  signOut: () => void
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: env.requireAuth ? null : LOCAL_USER,
  hasEntraAccount: false,
  isAccessDenied: false,
  // Nothing to wait for when auth is off — there is no MSAL startup sequence.
  isHydrated: !env.requireAuth,
  setAccount: (hasAccount) => set({ hasEntraAccount: hasAccount }),
  setUser: (user) => set({ user, isAccessDenied: false }),
  setAccessDenied: () => set({ user: null, isAccessDenied: true }),
  setHydrated: () => set({ isHydrated: true }),
  signOut: () => {
    set({ user: null, hasEntraAccount: false, isAccessDenied: false })
    void msalInstance?.logoutRedirect()
  },
}))

// A 401 from any request invalidates the session everywhere at once.
setUnauthorizedHandler(() => useAuthStore.getState().signOut())

export const useCurrentUser = () => useAuthStore((state) => state.user)
export const useIsAuthenticated = () => useAuthStore((state) => state.hasEntraAccount)

/** True when the signed-in user holds at least one of the given roles. */
export function useHasRole(...roles: PortalRole[]) {
  return useAuthStore((state) => {
    const held = state.user?.roles
    return held ? roles.some((role) => held.includes(role)) : false
  })
}
